// Callstack.
import compareMedia from '../sdp/compareMedia'
import getOperation from '../sdp/operations'
import getAffectedRemoteTracks, { getAllRemoteTracks } from '../sdp/remoteTracks'

// Call plugin.
import { callActions } from '../../call/interfaceNew/actions'
import * as actionTypes from '../../call/interfaceNew/actionTypes'
import { getOptions } from '../../call/interfaceNew/selectors'

// Call plugin helpers.
import { CALL_STATES, CALL_MEDIA_STATES } from '../../call/constants'
import { hasMediaFlowing, setMediaInactive } from '../../call/utils/sdp'
import { getMediaState } from '../../call/utils/state'
import { OPERATIONS, OP_STATUS } from '../../call/interfaceNew/constants'

// Other plugins.
import { logManager } from '../../logs'
import { recreatePeer, handleOffer, generateOffer } from '../../callstack/webrtc/midcall'
import { receivedAnswer, isSameSdpSessionId } from '../webrtc/negotiation'
import { getMediaById, getSessionById } from '../../webrtc/interface/selectors'

// Libraries.
import { all, call, put, select } from 'redux-saga/effects'

const log = logManager.getLogger('CALL')

/**
 * A "call update request" has been received and needs to be handled.
 * The request includes an offer SDP, so this is a regular webRTC scenario.
 *
 * This saga is intended to process all "call update requests", that have an
 *    SDP, received from the call's remote side. It is assumed the call has
 *    already been established, so this represents the start of the webRTC
 *    renegotiation process for the local side.
 * Handling the request is expected (but may not necessarily) affect the call's
 *    media. The call state will be updated depending on the requested changes
 *    and the current call state.
 * Assumptions:
 *    1. The request notification is valid.
 *        - Call exists, Call is not Ended, Call is able to receive a request.
 *    2. The SDP is a remote offer SDP.
 *        - Indicates regular webRTC negotiation should occur.
 * Responsibilities:
 *    1. Determine what the remote operation was (ie. what is being offered).
 *    2. Process the offer based on remote operation and current local state.
 *    3. Respond to the request.
 *    4. Update call state (via redux action).
 * @method handleUpdateRequest
 * @param {Object}   deps          Dependencies that the saga uses.
 * @param {Object}   deps.webRTC   The WebRTC stack.
 * @param {Object}   deps.requests The set of platform-specific signalling functions.
 * @param {Function} deps.requests.updateSessionResponse "Respond to offer" signalling function.
 * @param {Object}   targetCall    The call being acted on.
 * @param {Object}   params        Parameters of the update request.
 * @param {string}   params.sdp          A remote offer SDP.
 * @param {string}   params.remoteNumber Number of the remote participant.
 * @param {string}   params.remoteName   Name of the remote participant.
 */
export function * handleUpdateRequest (deps, targetCall, params) {
  const { webRTC, requests } = deps

  const log = logManager.getLogger('CALL', targetCall.id)
  log.info('Processing regular update request from remote endpoint.')

  let { sdp } = params
  const { remoteNumber, remoteName } = params

  const mediaState = yield call(getMediaState, targetCall)
  log.debug(`Current call info; State: ${targetCall.state}, MediaState: ${mediaState}.`)

  /*
   * Determine what the remote operation was. The remote operation and our
   *    current local state will affect how we process the received offer. Our
   *    response needs to reflect our desired local state, so that the remote
   *    side does not change the call in an undesired way (eg. offering media
   *    in a dual hold scenario should not reconnect media).
   */
  const session = yield call([webRTC.sessionManager, 'get'], targetCall.webrtcSessionId)
  if (!session) {
    log.debug(`webRTC session ${targetCall.webrtcSessionId} not found.`)
    return
  }
  /*
   * Use the currently set remote SDP and the newly-received remote SDP to
   *    determine what the remote operation was.
   */
  const remoteDesc = yield call([session, 'getLatestRemoteDesc'])
  let mediaDiff = yield call(compareMedia, remoteDesc.sdp, sdp)
  let remoteOp = yield call(getOperation, mediaDiff)

  log.debug(`Interpreted update request as a ${remoteOp} operation.`)
  /**
   * Check for remoteOp special-cases.
   *  This is needed for complex operations, where a few renegotiations occur
   *      as a single remote operation. Some of those renegotiations are
   *      special-cases (ie. not actual SDK operations).
   *  If the remoteOp is not "standard", then try to fit it to a "standard"
   *    operation that "fits best".
   */
  const isMediaFlowing = yield call(hasMediaFlowing, sdp)
  if (remoteOp === 'UNKNOWN') {
    /*
     * If the media state is remotely held but the latest renegotiation has
     *    media flowing, then handle it as an unhold.
     * This occurs:
     *  - As part of a remote direct transfer operation (when this user is being
     *      transferred to another user). When the transferee accepts the call,
     *      a renegotiation occurs that changes from 'MoH' to 'media flowing'
     *      state. This needs to be handled as an unhold by the SDK so that
     *      the call state is updated to reflect the media state.
     *  - As part of a remote consultative transfer operation (when this user was
     *      the first call specified in the transfer operation, but not for the
     *      second user). Same 'MoH' to 'media flowing' scenario as direct
     *      transfer.
     */
    if (mediaState === CALL_MEDIA_STATES.REMOTE_HOLD && isMediaFlowing) {
      remoteOp = OPERATIONS.UNHOLD
    } else {
      log.debug('Could not determine remote operation; retrying.')
      // Try it again, but this time match medias naively.
      mediaDiff = yield call(compareMedia, remoteDesc.sdp, sdp, true)
      remoteOp = yield call(getOperation, mediaDiff)
      log.debug(`Re-interpreted update request as a ${remoteOp} operation.`)
    }
  } else if (remoteOp === 'NO_CHANGE') {
    /*
     * Renegotiation did not change anything media related. This renegotiation
     *    may have been for a different purpose, such as changing the remote
     *    endpoint during a transfer.
     *
     * Let this remoteOp default to the generic "update call" operation except when we're on locally hold.
     */

    /*
     * When our media state is Local Hold, this means that our sdp has media as inactive.
     * Therefore, when we receive remote sdp with media as also inactive, this means the remote side is doing a hold operation.
     * Because of this, we get a remoteOp of `NO_CHANGE` when we actually want to handle it as a remote operation
     *  inorder to get into a dual hold state.
     */
    if (mediaState === CALL_MEDIA_STATES.LOCAL_HOLD) {
      remoteOp = OPERATIONS.HOLD
    }
  }

  log.info(`Processing update request as remote ${remoteOp} operation in ${mediaState} scenario.`)
  const callAction = yield call(getCallAction, remoteOp)

  /*
   * If the remote operation is offering media but the call is locally held,
   *    then we need to modify the offer to ensure that media does not restart.
   */
  if (isMediaFlowing && [CALL_MEDIA_STATES.DUAL_HOLD, CALL_MEDIA_STATES.LOCAL_HOLD].includes(mediaState)) {
    log.debug('Modifying remote offer to prevent resetting media while in local hold.')
    sdp = yield call(setMediaInactive, sdp)
    if (!sdp) {
      log.debug('SDP is either undefined or not a string.')
      return
    }
    // Update the `mediaDiff` after we modified the SDP.
    mediaDiff = yield call(compareMedia, remoteDesc.sdp, sdp, true)
  }

  /**
   * Decision: When we receive a remote offer SDP that has a different "session
   *    ID" as the currently set remote SDP, recreate the call's PeerConnection.
   * This is a workaround/fix for scenarios where the remote endpoint changes.
   *    It avoids possible DTLS role conflict issues.
   */
  // TODO: Also check fingerprint?
  const sameSession = yield call(isSameSdpSessionId, webRTC, targetCall.webrtcSessionId, sdp)
  if (!sameSession) {
    log.debug(`Received offer SDP is from a different session. Recreating Peer for call ${targetCall.id}.`)
    yield call(recreatePeer, webRTC, targetCall.webrtcSessionId)
  } else if (remoteOp === OPERATIONS.UNHOLD && targetCall.hasMOH) {
    /*
     * Edge-case: Even though its the same SDP session, the Peer still needs to
     *    be recreated.
     * The remote operation is an unhold but it is also stopping MoH. The Peer was
     *    recreated on start MOH because the SDP was from a different session, but
     *    the session doesn't change for stop MOH because it was a remote complex
     *    operation. The Peer needs to be recreated again otherwise local tracks
     *    will not be properly added back the call (because of how `recreatePeer` works).
     * Reference: KJS-934
     */
    log.debug(
      `Received offer SDP is both an unhold and stop MoH on the same session. Recreating Peer for call ${targetCall.id}.`
    )
    yield call(recreatePeer, webRTC, targetCall.webrtcSessionId)
  }

  /**
   * If the offer SDP has a DTLS role of active (which is invalid), change it to
   *    actpass. This is a workaround for transfer scenarios where the offer SDP
   *    is the remote endpoint's answer SDP from them answering the call. This
   *    happens when slow-start is not done for the transfer.
   */
  if (sdp.includes('setup:active')) {
    log.debug(`Received offer SDP with active role for call ${targetCall.id}. Changing.`)
    sdp = sdp.replace(/setup:active/g, 'setup:actpass')
  }

  // Handle the offer SDP to receive an answer SDP.
  // TODO: Handle the offer differently depending on remote operation.
  const { error, answerSDP } = yield call(handleOffer, deps, sdp, targetCall.webrtcSessionId, targetCall.bandwidth)

  if (error) {
    log.debug('Failed to receive offer SDP.', error)
    // TODO: Dispatch an error action to notify of the error scenario.
    // The call may now be in a bad state and needs to be fixed.
    return
  }

  // Get the list of remote tracks that are changing (either added or removed) as
  //    part of the remote operation. This is done after `handleOffer` so the
  //    Transceivers are up-to-date with the changes being offered.
  let remoteTracks = []
  if (!sameSession) {
    // If the PeerConnection was recreated, then ALL remote tracks will be new.
    remoteTracks = yield call(getAllRemoteTracks, session)
  } else {
    remoteTracks = yield call(getAffectedRemoteTracks, remoteOp, mediaDiff, session)
  }

  let affectedLocalTracks
  if (remoteOp === OPERATIONS.HOLD) {
    // On remote hold, all local tracks become unavailable.
    affectedLocalTracks = targetCall.localTracks
  } else if (remoteOp === OPERATIONS.UNHOLD && !targetCall.localHold) {
    // On remote unhold (that isn't a dual-hold), all local tracks on the Session
    //    become available.
    const sessionState = yield select(getSessionById, targetCall.webrtcSessionId)
    affectedLocalTracks = sessionState.localTracks
  }

  // Send answer sdp back to remote side
  const callInfo = {
    wrtcsSessionId: targetCall.wrtcsSessionId,
    answer: answerSDP,
    isAnonymous: targetCall.isAnonymous,
    account: targetCall.account,
    customParameters: targetCall.customParameters,
    customBodies: targetCall.customBodies
  }

  const response = yield call(requests.updateSessionResponse, callInfo)

  if (response.error) {
    // Scenario: The offer was processed, but failed to respond with the answer.
    // The remote side needs the answer SDP before the call is "connected".
    // TODO: Handle this scenario (retry request or fail/revert operation?)
    log.info('Failed to respond to remote offer with an answer.')
    yield put(
      callAction(targetCall.id, {
        remote: true,
        error: response.error
      })
    )
  } else {
    log.info('Finished responding to remote update. Changing state based on the remote operation.')
    yield put(
      callAction(targetCall.id, {
        remote: true,
        remoteParticipant: {
          displayNumber: remoteNumber,
          displayName: remoteName
        },
        // Include the affected tracks as part of the action so the call's
        //    state can be updated accordingly.
        localTracks: affectedLocalTracks,
        remoteTracks
      })
    )
  }
}

/**
 * A "call update request" has been received and needs to be handled.
 * The request does not include an SDP, so this is a slow start webRTC scenario.
 *
 * This saga is intended to process all "call update requests", that have no
 *    SDP, received from the call's remote side. It is assumed the call has
 *    already been established, so this represents the start of the slow start
 *    webRTC renegotiation process for the local side.
 * Handling the request will not affect the call's media at this point. As
 *    opposed to regular negotiation, we require an additional "call response"
 *    notification before negotiation is complete. Media and call state will
 *    only be affected by that response notification.
 * Assumptions:
 *    1. The request notification is valid.
 *        - Call exists, Call is not Ended, Call is able to receive a request.
 *    2. There is no SDP.
 *        - Indicates slow start webRTC negotiation should occur.
 * Responsibilities:
 *    1. Generate an offer SDP based on our current local state.
 *    2. Respond to the request with our offer.
 * @method handleSlowUpdateRequest
 * @param {Object}   deps          Dependencies that the saga uses.
 * @param {Object}   deps.webRTC   The WebRTC stack.
 * @param {Object}   deps.requests The set of platform-specific signalling functions.
 * @param {Function} deps.requests.updateSessionResponse "Respond to offer" signalling function.
 * @param  {Object} targetCall The call being acted on.
 * @param {Object}   params       Parameters of the update request.
 * @param {string}   params.remoteNumber Number of the remote participant.
 * @param {string}   params.remoteName   Name of the remote participant.
 */
export function * handleSlowUpdateRequest (deps, targetCall, params) {
  const { webRTC, requests } = deps
  const { remoteNumber, remoteName } = params

  const log = logManager.getLogger('CALL', targetCall.id)
  log.info('Processing slow-start update request from remote endpoint.')

  const mediaState = yield call(getMediaState, targetCall)
  log.debug(`Current call info; State: ${targetCall.state}, MediaState: ${mediaState}.`)

  /**
   * Decision: Whenever we receive a slow-start notification, recreate the Peer.
   *    This is needed in certain situations (but not all...)
   * Known Scenarios:
   * 1. Link direct transfer:
   *    - The user being transferred will be on remote hold and will receive a
   *        slow-start notification as part of the "transfer renegotitions".
   *        Not recreating the Peer will result in a DTLS role conflict.
   *    - The user receiving the transfer will negotiate the initial connection,
   *        then will receive a slow-start notification (when Connected). Not
   *        recreating the Peer for the slow-start results in DTLS conflict.
   * Exception: If client has set the call option 'mediaBrokerOnly' to true, then
   *    we don't need to recreate the call's PeerConnection.
   */
  const { mediaBrokerOnly } = yield select(getOptions)
  if (!mediaBrokerOnly) {
    log.debug(`Recreating Peer for Session ${targetCall.webrtcSessionId}.`)
    yield call(recreatePeer, webRTC, targetCall.webrtcSessionId)
  } else {
    log.debug('Not recreating PeerConnection as mediaBrokerOnly is set to true in configuration.')
  }
  /*
   * Decision: Whenever we receive an offer without an SDP (slow start), and
   *    we're not locally held, respond with all media directions as sendrecv.
   *    This is to signal what we are capable of, rather than what we want.
   *    When we are locally held, we don't want to allow the remote side to
   *    restart media, hence respond with media directions as inactive.
   * This handles two (non-local hold) scenarios specifically:
   *  1. Re-invite: We have already negotiated to perform the remote operation,
   *      but we received a second offer (without an SDP) as a side-effect to
   *      further update media.
   *    Eg. Music on Hold: The remote operation is to set media to inactive,
   *      and the re-invite updates media so we can receive the hold audio.
   *  2. Slow start unhold: We receive an offer without an SDP, but we have no
   *      idea what the remote operation was. If we are on remote hold, we
   *      assume that it's a remote unhold operation.
   */
  const mediaDirections = {
    audio: targetCall.localHold ? 'inactive' : 'sendrecv',
    video: targetCall.localHold ? 'inactive' : 'sendrecv'
  }

  // Create an offer to use for responding to the slow start notification.
  const slowOffer = yield call(generateOffer, deps, targetCall.webrtcSessionId, mediaDirections)

  const callInfo = {
    wrtcsSessionId: targetCall.wrtcsSessionId,
    answer: slowOffer.sdp,
    isAnonymous: targetCall.isAnonymous,
    account: targetCall.account,
    customParameters: targetCall.customParameters,
    customBodies: targetCall.customBodies
  }

  // Respond with our "offer".
  const response = yield call(requests.updateSessionResponse, callInfo)

  if (response.error) {
    // TODO: Handle this scenario (retry request or fail/revert operation?)
    log.info('Failed to respond to slow-start remote update.', response.error)
    return
  }

  log.info('Finished responding to slow-start remote update. Waiting on remote response.')
  /*
   * The operation is not complete until we receive a response to our offer. The
   *    response will be handled by the `handleSlowUpdateResponse` saga.
   */
  yield put(
    callActions.remoteSlowStart(targetCall.id, {
      remoteParticipant: {
        displayNumber: remoteNumber,
        displayName: remoteName
      }
    })
  )
}

/**
 * A "call update response" has been received and needs to be handled.
 * The response is for a local operation, so this a regular webRTC scenario.
 *
 * This saga is intended to process all "call update responses", that are from
 *    local operations, received from the call's remote side. The call may or
 *    may not be established at this point. This represents the end of the
 *    regular webRTC negotiation process for the local side.
 * Handling the response will affect the call's media, and the call state will
 *    be updated depending on the new media and the current call state.
 * Assumptions:
 *    1. Ensure this is a valid notification.
 *        - Call exists, Call is not ended, Call expects the response.
 *    2. The original operation was started locally.
 *        - Indicates that it was a regular negotiation process.
 * Responsibilities:
 *    1. Have the callstack process the answer SDP.
 *    2. TODO: Determine what the original operation was.
 *    3. Update call state (via redux action).
 * @method handleUpdateResponse
 * @param {Object}  deps          Dependencies that the saga uses.
 * @param {Object}  deps.webRTC   The WebRTC stack.
 * @param {Object}  deps.requests The set of platform-specific signalling functions.
 * @param  {Object} targetCall The call being acted on.
 * @param  {Object} params
 * @param  {string} params.sdp A remote answer SDP.
 * @param  {string} params.remoteName   Name of the remote participant.
 * @param  {string} params.remoteNumber Number of the remote participant.
 */
export function * handleUpdateResponse (deps, targetCall, params) {
  const { sdp } = params

  const log = logManager.getLogger('CALL', targetCall.id)
  log.info('Processing remote response from regular, local update request.')

  const mediaState = yield call(getMediaState, targetCall)
  log.debug(`Current call info; State: ${targetCall.state}, MediaState: ${mediaState}.`)

  // Handle the remote answer SDP.
  const sessionInfo = { sessionId: targetCall.webrtcSessionId, answerSdp: sdp }
  const error = yield call(receivedAnswer, deps, sessionInfo, targetCall)

  if (error) {
    log.debug('Failed to receive answer SDP', error)
    // TODO: Better error handling.
    yield put(
      callActions.callAccepted(targetCall.id, {
        state: CALL_STATES.ENDED,
        error
      })
    )
    return
  }

  const localOp = targetCall.localOp

  // Update call state depending on what the current call state is.
  if ([CALL_STATES.RINGING, CALL_STATES.INITIATED, CALL_STATES.EARLY_MEDIA].includes(targetCall.state)) {
    log.info(`Handling state change as remote answer operation. Changing to ${CALL_STATES.CONNECTED}.`)

    // Get the list of all remote tracks being offered in this call. This is a
    //    new call, so this should be the full list of remote tracks available.
    const session = yield call([deps.webRTC.sessionManager, 'get'], targetCall.webrtcSessionId)
    const remoteTracks = yield call(getAllRemoteTracks, session)
    // Get the list of all local tracks on the Session.
    const sessionState = yield select(getSessionById, targetCall.webrtcSessionId)
    const localTracks = sessionState.localTracks

    // Scenario: The call was previously ringing, so this response is a "call
    //    accepted" notification. The call should now be established.
    // It's possible that the call never entered Ringing state (from Initiated).
    //    Handle Initiated the same as Ringing.
    // Transition to Connected state.
    if (localOp && localOp.status === OP_STATUS.PENDING && localOp.operation === OPERATIONS.MAKE) {
      yield put(
        callActions.makeCallFinish(targetCall.id, {
          state: CALL_STATES.CONNECTED,
          // TODO: Make sure this is the correct units
          startTime: Date.now(),
          // Remote participant's information.
          remoteParticipant: {
            displayNumber: params.remoteNumber,
            displayName: params.remoteName
          },
          // Add the remote tracks to the call state.
          remoteTracks
          // Local tracks have already been added on "pending make call".
        })
      )
    } else {
      // TODO: Still need this for complex Direct Transfer & Join (remote side). Figure out a better way.
      yield put(
        callActions.callAccepted(targetCall.id, {
          // TODO: Determine when we're actually "in call".
          state: CALL_STATES.CONNECTED,
          // TODO: Make sure this is the correct units
          startTime: Date.now(),
          // Remote participant's information.
          remoteParticipant: {
            displayNumber: params.remoteNumber,
            displayName: params.remoteName
          },
          // Add the remote tracks to the call state.
          remoteTracks,
          localTracks
        })
      )
    }
  } else if (targetCall.state === CALL_STATES.CONNECTED || targetCall.state === CALL_STATES.ON_HOLD) {
    // Scenario: The call was previously established, so this response is "call
    //    update" notification. The call is still established.
    // Transition to next state depending on what the operation was and the
    //    updated media.
    let affectedRemoteTracks
    let affectedLocalTracks
    if (localOp && localOp.status === OP_STATUS.PENDING) {
      let finishAction
      switch (localOp.operation) {
        case OPERATIONS.HOLD:
          finishAction = callActions.holdCallFinish
          // For a local hold, we expect all current tracks to become unavailable.
          affectedRemoteTracks = targetCall.remoteTracks
          affectedLocalTracks = targetCall.localTracks
          break
        case OPERATIONS.UNHOLD: {
          finishAction = callActions.unholdCallFinish
          // For a local unhold, we need to look at the SDP and what tracks are
          //    available in the Session to know which remote tracks will be re-added
          //    by this operation.
          const session = yield call([deps.webRTC.sessionManager, 'get'], targetCall.webrtcSessionId)
          affectedRemoteTracks = yield call(getAllRemoteTracks, session)

          // If we're not in dual-hold (ie. we will be Connected after this), then
          //    local tracks will become available.
          if (!targetCall.remoteHold) {
            // For local tracks, just need to get the local tracks the Session has.
            const sessionState = yield select(getSessionById, targetCall.webrtcSessionId)
            affectedLocalTracks = sessionState.localTracks
          }
          break
        }
        case OPERATIONS.ADD_BASIC_MEDIA:
        case OPERATIONS.ADD_MEDIA: {
          finishAction = callActions.addMediaFinish

          // Get the list of track IDs added to the call by this operation.
          const medias = yield all(localOp.operationData.mediaIds.map(id => select(getMediaById, id)))
          affectedLocalTracks = []
          medias.forEach(media => {
            affectedLocalTracks = affectedLocalTracks.concat(media.tracks)
          })
          break
        }

        case OPERATIONS.REMOVE_BASIC_MEDIA:
        case OPERATIONS.REMOVE_MEDIA: {
          finishAction = callActions.removeMediaFinish

          // Get the list of track IDs removed from the call by this operation.
          affectedLocalTracks = localOp.operationData.tracks
          break
        }
        case OPERATIONS.MEDIA_RESTART:
          finishAction = callActions.restartMediaFinish
      }
      if (finishAction) {
        log.info(
          `Finished processing remote response to local ${localOp.operation}. Changing state based on operation.`
        )
        const action = finishAction(targetCall.id, localOp.operationData)
        // If the local operation affected tracks, then pass that info to
        //    the action as well to keep state up-to-date.
        if (affectedLocalTracks) {
          action.payload.localTracks = affectedLocalTracks
        }
        if (affectedRemoteTracks) {
          action.payload.remoteTracks = affectedRemoteTracks
        }
        yield put(action)
        return
      }
    }

    // TODO: Leave this here for now to not break other operations. Need to update this as we convert the other operations.
    // Determine whether the SDP has active media.
    const mediaFlowing = yield call(hasMediaFlowing, params.sdp)
    const nextState = mediaFlowing ? CALL_STATES.CONNECTED : CALL_STATES.ON_HOLD

    log.info(`Handling state change as local midcall operation ${mediaFlowing ? 'with' : 'without'} media flowing.`)
    log.info(`Finished processing remote response. Changing to ${nextState}.`)
    yield put(
      callActions.updateCall(targetCall.id, {
        state: nextState
      })
    )
  } else {
    // Scenario: The call is in an unexpected state for receiving a remote
    //    answer SDP. This should never happen.
    log.error('The call is in a bad state for receiving response. Cannot update.')
  }
}

/**
 * A "call update response" has been received and needs to be handled.
 * The response is for a remote operation, so this a slow start webRTC scenario.
 *
 * This saga is intended to process all "call update responses", that are from
 *    remote operations, received from the call's remote side. This represents
 *    the end of slow start webRTC negotiation process for the local side.
 * Handling the response will affect the call's media, and the call state will
 *    be updated depending on the new media and the current call state.
 * Assumptions:
 *    1. Ensure this is a valid notification.
 *        - Call exists, Call is not ended, Call expects the response.
 *    2. The original operation was started remotely.
 *        - Indicates that it a was slow start negotiation process.
 *        - The call has a pending remote operation.
 * Responsibilities:
 *    1. Determine what the original operation was.
 *    2. Have the callstack process the answer SDP.
 *    3. Update call state (via redux action) based on original operation.
 * @method handleSlowUpdateResponse
 * @param {Object}  deps          Dependencies that the saga uses.
 * @param {Object}  deps.webRTC   The WebRTC stack.
 * @param {Object}  deps.requests The set of platform-specific signalling functions.
 * @param  {Object} targetCall The call being acted on.
 * @param  {Object} params
 * @param  {string} params.sdp A remote answer SDP.
 * @param  {string} params.remoteName   Name of the remote participant.
 * @param  {string} params.remoteNumber Number of the remote participant.
 */
export function * handleSlowUpdateResponse (deps, targetCall, params) {
  let { sdp } = params

  const log = logManager.getLogger('CALL', targetCall.id)
  log.info('Processing remote response from slow-start remote update request.')

  const mediaState = yield call(getMediaState, targetCall)
  log.debug(`Current call info; State: ${targetCall.state}, MediaState: ${mediaState}.`)

  /*
   * Determine what the remote operation was. The remote operation and our
   *    current local state will affect how we update the call's state.
   */
  const session = yield call([deps.webRTC.sessionManager, 'get'], targetCall.webrtcSessionId)
  if (!session) {
    log.debug(`webRTC session ${targetCall.webrtcSessionId} not found.`)
    return
  }
  /*
   * Use the currently set remote SDP and the newly-received remote SDP to
   *    determine what the remote operation was.
   */
  const remoteDesc = yield call([session, 'getLatestRemoteDesc'])
  let mediaDiff = yield call(compareMedia, remoteDesc.sdp, sdp)

  let remoteOp = yield call(getOperation, mediaDiff)
  log.debug(`Interpreted slow-start response as a ${remoteOp} operation.`)
  const mediaFlowing = yield call(hasMediaFlowing, sdp)

  if (remoteOp === 'UNKNOWN') {
    log.debug('Could not determine remote operation; retrying.')
    // Try it again, but this time match medias naively.
    mediaDiff = yield call(compareMedia, remoteDesc.sdp, sdp, true)
    remoteOp = yield call(getOperation, mediaDiff)
    log.debug(`Re-interpreted slow-start response as a ${remoteOp} operation.`)
  }

  /**
   * Special case: Assume the remote operation was actually a Hold.
   * The operation was interpreted as "no change" because the remote media did
   *    not change in the remote operation. But all media is inactive and we are
   *    in a Local Hold state. This means that the previous media was inactive
   *    because of our local hold. So the new media being inactive is a change
   *    that was done on the remote side, ie. it was actually a remote hold.
   */
  if (remoteOp === 'NO_CHANGE' && !mediaFlowing && mediaState === CALL_MEDIA_STATES.LOCAL_HOLD) {
    remoteOp = OPERATIONS.HOLD
    log.debug(`Interpreting NO_CHANGE operation as ${remoteOp} due to ${mediaState} state.`)
  }

  log.info(`Handling state change as remote ${remoteOp} in ${mediaState} scenario.`)

  /*
   * If the remote operation was an unhold and we're in a dual hold scenario,
   *    then make sure the remote SDP can't restart media. Do this by ensuring
   *    that all media directions in the remote SDP are inactive.
   *
   * This won't affect the remote end, but it prevents the local side from
   *    thinking it is receiving a track when it shouldn't be.
   *
   * This can occur during slow start renegotiation, if the server doesn't
   *    respect the media directions we set (assuming we set them correctly).
   */
  if (remoteOp === OPERATIONS.UNHOLD && targetCall.localHold === true) {
    const undesired = ['sendrecv', 'sendonly', 'recvonly']
    // If any undesirable direction is found in the SDP, replace it with inactive.
    if (undesired.some(dir => sdp.includes(dir))) {
      log.debug('Modifying remote SDP to prevent media in local hold scenario.')
      sdp = sdp.replace(/sendrecv|sendonly|recvonly/gi, 'inactive')
      // Update the `mediaDiff` after we modified the SDP.
      mediaDiff = yield call(compareMedia, remoteDesc.sdp, sdp, true)
    }
  }

  // Handle the remote answer SDP.
  const sessionInfo = { sessionId: targetCall.webrtcSessionId, answerSdp: sdp }
  const error = yield call(receivedAnswer, deps, sessionInfo, targetCall)

  if (error) {
    log.debug('Failed to receive answer SDP', error)
    // TODO: Dispatch an error action to notify of the error scenario.
    // The call may now be in a bad state and needs to be fixed.
    return
  }

  if (targetCall.state === CALL_STATES.CONNECTED || targetCall.state === CALL_STATES.ON_HOLD) {
    const callAction = yield call(getCallAction, remoteOp)
    const nextState = mediaFlowing ? CALL_STATES.CONNECTED : CALL_STATES.ON_HOLD

    log.info(`Finished processing remote slow-start response. Changing to ${nextState}.`)

    // Get the list of remote tracks that are changing (either added or removed) as
    //    part of the remote operation. This is done after `handleOffer` so the
    //    Transceivers are up-to-date with the changes being offered.
    const affectedRemoteTracks = yield call(getAffectedRemoteTracks, remoteOp, mediaDiff, session)

    let affectedLocalTracks = []
    // If we're not in dual-hold (ie. we will be Connected after this), then
    //    local tracks will become available.
    if (remoteOp === OPERATIONS.UNHOLD && !targetCall.localHold) {
      // For local tracks, just need to get the local tracks the Session has.
      const sessionState = yield select(getSessionById, targetCall.webrtcSessionId)
      affectedLocalTracks = sessionState.localTracks
    }

    const action = callAction(targetCall.id, {
      state: nextState,
      // Remote participant's information.
      remoteParticipant: {
        displayNumber: params.remoteNumber,
        displayName: params.remoteName
      },
      // Add the remote tracks to the call state.
      remoteTracks: affectedRemoteTracks,
      localTracks: affectedLocalTracks
    })

    if (action.type === actionTypes.UPDATE_CALL) {
      /*
       * This is a hack. The proper solution would be to never use a generic action for state changes.
       * This action doesn't work with the SDK's "operation tracking" logic in the call reducers, so
       *     the call state becomes out-of-sync with the operations.
       *  In this scenario, the operation start was a remote slow-start negotiation, but the SDK fails
       *      to determine what the actual operation was, so it falls-back to using the generic action
       *      for the "finish". This means that the remoteOp in state never gets cleaned-up, which can
       *      cause issues later.
       *  The workaround is to mark the action as a remote update action, so that the reducer can
       *      manually clean-up the remote operation in this scenario.
       * Reference: KJS-542, , KJS-571
       */
      action.meta = {
        isRemote: true
      }
    }

    yield put(action)
  } else {
    // Scenario: The call is in an unexpected state for receiving a remote
    //    answer SDP. This should never happen.
    log.error('The call is in a bad state for receiving response. Cannot update.')
  }
}

/**
 * Helper function that returns a call action function based on the
 * remote operation being requested
 * TODO: Move this somewhere better.
 * TODO: Do this differently / better.
 * @method getCallAction
 * @param (string) remoteOp Remote operation being requested
 * @returns {function} A redux action creator
 */
export function * getCallAction (remoteOp) {
  // Determine what redux action the operation represents.
  switch (remoteOp) {
    case OPERATIONS.HOLD:
      return callActions.remoteHoldFinish
    case OPERATIONS.UNHOLD:
      return callActions.remoteUnholdFinish
    case OPERATIONS.ADD_MEDIA:
      return callActions.remoteAddMediaFinish
    case OPERATIONS.REMOVE_MEDIA:
      return callActions.remoteRemoveMediaFinish
    case OPERATIONS.START_MOH:
      return callActions.remoteStartMohFinish
    case OPERATIONS.STOP_MOH:
      return callActions.remoteStopMohFinish
    case OPERATIONS.SLOW_START:
      return callActions.remoteSlowStart
  }

  log.debug(`Call action for ${remoteOp} not known; using generic action.`)
  return callActions.updateCall
}
